/**
 * Application entry point.
 * Replaces the monolithic app.js — wires together all panel modules.
 */

import { appConfig } from "./config";
import { createProvider } from "./data/provider";
import { setProvider } from "./providerInstance";
import { getState, updateState, subscribe } from "./state";
import { els } from "./dom";
import { checkAuth, bindLoginModal, handleAuthCallback, bindLogout } from "./auth";
import { bindNavigation, navigateTo } from "./router";
import { normalizeRole } from "./utils/formatting";
import { escapeHtml } from "./utils/escapeHtml";
import { search } from "./services/search";
import { DataCache } from "./cache";
import { setCache } from "./cacheInstance";
import { setRefreshFn } from "./refreshService";
import { bindKeyboardShortcuts } from "./services/keyboard";
import { friendlyError } from "./services/errorMessages";
import { refreshNotifications, markAllRead } from "./services/notifications";
import { renderNotificationBell } from "./components/NotificationBell";

import * as dashboard from "./panels/dashboard";
import * as entry from "./panels/entry";
import * as matrix from "./panels/matrix";
import * as booking from "./panels/booking";
import * as admin from "./panels/admin";
import * as bookingMatrix from "./panels/bookingMatrix";
import {
  enrichAssetsWithVolume,
  buildReviewedMasterIdSet,
  setFormMessage,
} from "./panels/shared";

// ── Bootstrap provider ───────────────────────────────────────

const provider = createProvider(appConfig);
setProvider(provider);

// ── Data cache ───────────────────────────────────────────────

const dataCache = new DataCache(appConfig.refreshIntervalMs || 5 * 60 * 1000);
setCache(dataCache);

// ── Data refresh ─────────────────────────────────────────────

async function refreshData(forceRefresh = false): Promise<void> {

  if (forceRefresh) {
    dataCache.invalidate();
  }

  try {
    const cachedAssets = dataCache.get<any[]>("assets");
    const cachedForecasts = dataCache.get<any[]>("forecasts");
    const cachedRules = dataCache.get<any[]>("rules");

    const assets = cachedAssets ?? await provider.listAssets();
    const forecasts = cachedForecasts ?? await provider.listForecasts();
    const rules = cachedRules ?? await provider.listRules();

    // Store fetched data in cache
    if (!cachedAssets) dataCache.set("assets", assets);
    if (!cachedForecasts) dataCache.set("forecasts", forecasts);
    if (!cachedRules) dataCache.set("rules", rules);

    const enrichedAssets = enrichAssetsWithVolume(assets, rules);

    updateState({
      assets: enrichedAssets,
      forecasts,
      rules,
      reviewedMasterIds: buildReviewedMasterIdSet(enrichedAssets, forecasts),
    });

    // Panel subscriptions handle re-rendering automatically.

    refreshNotifications(); // non-blocking, no await

    const now = new Date().toLocaleString();
    if (els.lastSync) els.lastSync.textContent = `Last sync: ${now}`;
  } catch (err) {
    console.error("refreshData() failed:", err);
    throw err;
  }
}

// Register refreshData so panel modules can call it without circular imports.
setRefreshFn(refreshData);

// ── Periodic refresh ─────────────────────────────────────────

function startPeriodicRefresh(): void {
  if (!appConfig.refreshIntervalMs || appConfig.refreshIntervalMs < 1) return;
  setInterval(() => {
    refreshData().catch(() => {
      // User can still trigger manual refresh.
    });
  }, appConfig.refreshIntervalMs);
}

// audit M1 — cross-panel renderer wiring was removed. Each panel now
// subscribes to the state keys it cares about (assets/forecasts/rules/
// reviewedMasterIds) and re-renders independently. onPanelActivate()
// handles the moment the user switches back to a panel.

// ── Sidebar toggle (responsive) ─────────────────────────────

function bindSidebarToggle(): void {
  const toggle = document.getElementById("sidebar-toggle");
  const sidebar = document.querySelector(".sidebar");
  if (!toggle || !sidebar) return;

  toggle.addEventListener("click", () => {
    const isOpen = sidebar.classList.toggle("is-open");
    if (isOpen) {
      const backdrop = document.createElement("div");
      backdrop.className = "sidebar-backdrop";
      backdrop.addEventListener("click", () => {
        sidebar.classList.remove("is-open");
        backdrop.remove();
      });
      document.body.appendChild(backdrop);
    } else {
      document.querySelector(".sidebar-backdrop")?.remove();
    }
  });

  // Close sidebar when a nav item is clicked (mobile)
  sidebar.addEventListener("click", (e) => {
    const navItem = (e.target as HTMLElement).closest(".nav-item");
    if (navItem && sidebar.classList.contains("is-open")) {
      sidebar.classList.remove("is-open");
      document.querySelector(".sidebar-backdrop")?.remove();
    }
  });
}

// ── Global search ───────────────────────────────────────────

function bindGlobalSearch(): void {
  const input = document.getElementById("global-search") as HTMLInputElement | null;
  const dropdown = document.getElementById("search-results");
  if (!input || !dropdown) return;

  let debounceTimer: ReturnType<typeof setTimeout>;

  input.addEventListener("input", () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      const query = input.value.trim();
      const results = search(query);

      if (!query || query.length < 2) {
        dropdown.classList.add("hidden");
        return;
      }

      if (!results.length) {
        dropdown.innerHTML = `<div class="search-result-empty">No results for "${escapeHtml(query)}"</div>`;
      } else {
        dropdown.innerHTML = results.map((r) =>
          `<div class="search-result-item" data-search-panel="${r.panel}" data-search-id="${escapeHtml(r.id)}">
            <div class="search-result-title">${escapeHtml(r.title)}</div>
            <div class="search-result-subtitle">${escapeHtml(r.subtitle)}</div>
          </div>`
        ).join("");
      }
      dropdown.classList.remove("hidden");
    }, 200);
  });

  dropdown.addEventListener("click", (e) => {
    const item = (e.target as HTMLElement).closest<HTMLElement>(".search-result-item");
    if (!item) return;
    const panel = item.dataset.searchPanel || "dashboard";
    navigateTo(panel);
    input.value = "";
    dropdown.classList.add("hidden");
  });

  // Close on click outside
  document.addEventListener("click", (e) => {
    if (!(e.target as HTMLElement).closest(".topbar-search")) {
      dropdown.classList.add("hidden");
    }
  });

  // Close on Escape
  input.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      input.value = "";
      dropdown.classList.add("hidden");
    }
  });
}

// ── Notification bell ───────────────────────────────────────

function bindNotificationBell(): void {
  const container = document.getElementById("notif-bell-container");
  if (!container) return;

  subscribe("notifications", () => {
    const state = getState();
    renderNotificationBell(container, state.notifications, state.unreadCount);
  });

  // Toggle dropdown
  container.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest("#notif-bell-btn");
    if (btn) {
      const dropdown = document.getElementById("notif-dropdown");
      dropdown?.classList.toggle("hidden");
      return;
    }
    // Mark all read
    if ((e.target as HTMLElement).id === "notif-mark-all") {
      markAllRead();
      return;
    }
    // Click notification item
    const item = (e.target as HTMLElement).closest<HTMLElement>("[data-notif-id]");
    if (item) {
      const panel = item.dataset.notifPanel;
      if (panel) navigateTo(panel);
      document.getElementById("notif-dropdown")?.classList.add("hidden");
    }
  });

  // Close on click outside
  document.addEventListener("click", (e) => {
    if (!(e.target as HTMLElement).closest(".notif-bell-container")) {
      document.getElementById("notif-dropdown")?.classList.add("hidden");
    }
  });

  // Initial render with empty state
  renderNotificationBell(container, [], 0);
  // Non-blocking fetch
  refreshNotifications();
}

// ── Init ─────────────────────────────────────────────────────

async function init(): Promise<void> {
  bindSidebarToggle();
  bindGlobalSearch();
  bindKeyboardShortcuts();

  // Initialize all panels (registers onPanelActivate callbacks + binds events)
  dashboard.init();
  entry.init();
  matrix.init();
  booking.init();
  bookingMatrix.init();
  admin.init();

  bindNavigation();
  bindLogout();
  bindNotificationBell();

  // Role selector
  if (els.roleSelect) {
    const state = getState();
    (els.roleSelect as HTMLSelectElement).value = state.role;
    els.roleSelect.addEventListener("change", () => {
      const newRole = normalizeRole((els.roleSelect as HTMLSelectElement).value);
      updateState({ role: newRole });
      dashboard.applyRole(newRole);
      setFormMessage(`Role switched to ${newRole}.`);
    });
  }

  // Provider name
  if (els.providerName) {
    els.providerName.textContent = provider.name;
  }

  // Refresh button — forces cache invalidation
  if (els.refreshBtn) {
    els.refreshBtn.addEventListener("click", async () => {
      els.dataLoading?.classList.remove("hidden");
      try {
        await refreshData(true);
        setFormMessage("Data refreshed.");
      } catch (err) {
        setFormMessage(friendlyError(err), true);
      } finally {
        els.dataLoading?.classList.add("hidden");
      }
    });
  }

  // Apply initial role visibility
  dashboard.applyRole(getState().role);

  // Load data (spinner is visible by default in HTML)
  try {
    await refreshData();
  } finally {
    els.dataLoading?.classList.add("hidden");
  }
  startPeriodicRefresh();
}

// ── Auth gate ────────────────────────────────────────────────

(async () => {
  handleAuthCallback();
  const isAuthenticated = await checkAuth();

  if (!isAuthenticated) {
    els.dataLoading?.classList.add("hidden");
    bindLoginModal();
    return;
  }

  init().catch((err) => {
    console.error("init() failed:", err);
    setFormMessage(friendlyError(err), true);
  });
})();
